"use client";
import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const slides = [
{
  id: 1,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_13f57cb3f-1772906006056.png",
  alt: 'Construction workers on a building site in Qatar wearing safety equipment',
  tag: "Qatar\'s Premier Manpower Partner",
  headline: 'BUILDING QATAR\'S FUTURE WORKFORCE',
  body: 'Delivering pre-screened, compliant, and ready-to-deploy talent across every major construction and infrastructure project in the nation.',
  cta1: { label: 'Request a Proposal →', href: '#contact' },
  cta2: { label: 'Our Services', href: '#services' },
  kenBurns: 'ken-burns-active'
},
{
  id: 2,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17f36a980-1772147251673.png",
  alt: 'Oil refinery at dusk with industrial pipes and blue sky',
  tag: 'Oil, Gas & Energy Sector',
  headline: 'POWERING CRITICAL INDUSTRIES',
  body: 'Specialized workforce solutions for Qatar\'s most demanding energy and petrochemical operations, from upstream exploration to downstream processing.',
  cta1: { label: 'View Sectors →', href: '#sectors' },
  cta2: { label: 'Hire Now', href: '#contact' },
  kenBurns: 'ken-burns-active-2'
},
{
  id: 3,
  image: "https://images.unsplash.com/photo-1610536694908-31795916de9b",
  alt: 'Modern skyscrapers and cranes against a clear blue sky',
  tag: 'Infrastructure & Construction',
  headline: 'RAISING QATAR\'S SKYLINE',
  body: 'From foundation to finishing, our skilled tradespeople and site supervisors power Qatar\'s most iconic infrastructure projects.',
  cta1: { label: 'Explore Services →', href: '#services' },
  cta2: { label: 'About Us', href: '#about' },
  kenBurns: 'ken-burns-active'
},
{
  id: 4,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1478bcc45-1764651525877.png",
  alt: 'Diverse business team collaborating around a table in modern office',
  tag: 'HR Management & Consulting',
  headline: 'TALENT THAT TRULY DELIVERS',
  body: 'Strategic HR consulting and dedicated account management that aligns your workforce strategy with Qatar\'s Vision 2030 objectives.',
  cta1: { label: 'Find Talent →', href: '#contact' },
  cta2: { label: 'All Services', href: '#services' },
  kenBurns: 'ken-burns-active-2'
},
{
  id: 5,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e9010aab-1772738299529.png",
  alt: 'Busy container port with cranes loading cargo ships at sunset',
  tag: 'Logistics, Airport & Seaport',
  headline: 'MOVING QATAR FORWARD',
  body: 'End-to-end staffing for Qatar\'s logistics, aviation, and maritime sectors — keeping the nation\'s supply chains running 24/7.',
  cta1: { label: 'View Sectors →', href: '#sectors' },
  cta2: { label: 'Contact Us', href: '#contact' },
  kenBurns: 'ken-burns-active'
}];


const stats = [
{ value: '8+', label: 'Years Experience' },
{ value: '1,500+', label: 'Active Workforce' },
{ value: '50,000+', label: 'Pre-Qualified Talent' },
{ value: '12', label: 'Industry Sectors' }];


export default function HeroSlider() {
  const [current, setCurrent] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const goTo = useCallback((index: number) => {
    setCurrent(index);
    setAnimKey((k) => k + 1);
  }, []);

  const next = useCallback(() => {
    goTo((current + 1) % slides.length);
  }, [current, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + slides.length) % slides.length);
  }, [current, goTo]);

  useEffect(() => {
    timerRef.current = setTimeout(next, 6000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [next]);

  const handleScroll = (href: string) => {
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const slide = slides[current];

  return (
    <section id="home" className="relative w-full h-screen min-h-[600px] overflow-hidden">
      {/* Background Image with Ken Burns */}
      {slides.map((s, i) =>
      <div
        key={s.id}
        className={`absolute inset-0 transition-opacity duration-1000 ${i === current ? 'opacity-100' : 'opacity-0'}`}>
        
          <div
          key={`kb-${i}-${animKey}`}
          className={`absolute inset-0 ${i === current ? s.kenBurns : ''}`}>
          
            <AppImage
            src={s.image}
            alt={s.alt}
            fill
            priority={i === 0}
            className="object-cover"
            sizes="100vw" />
          
          </div>
        </div>
      )}

      {/* Dark overlay */}
      <div className="absolute inset-0 z-10" style={{ background: 'rgba(2,13,31,0.65)' }} />

      {/* Gradient overlay bottom */}
      <div
        className="absolute bottom-0 left-0 right-0 h-48 z-10"
        style={{ background: 'linear-gradient(to top, rgba(2,13,31,1) 0%, transparent 100%)' }} />
      

      {/* Mesh glow */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 30% 50%, rgba(0,194,255,0.08) 0%, transparent 60%)'
        }} />
      

      {/* Content */}
      <div className="relative z-20 h-full flex items-center">
        <div className="max-w-[1280px] mx-auto px-6 w-full">
          <div key={`content-${current}-${animKey}`} className="max-w-3xl">
            {/* Tag */}
            <div className="slide-tag inline-flex items-center gap-2 mb-6">
              <span
                className="px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[2px] text-white"
                style={{
                  background: 'rgba(0,194,255,0.15)',
                  border: '1px solid rgba(0,194,255,0.3)',
                  fontFamily: 'Plus Jakarta Sans, sans-serif'
                }}>
                
                {slide.tag}
              </span>
            </div>

            {/* Headline */}
            <h1
              className="slide-headline hero-headline font-extrabold text-fg mb-6 leading-[1.05]"
              style={{
                fontFamily: 'Syne, sans-serif',
                fontSize: 'clamp(2.25rem, 5vw, 4.5rem)',
                textShadow: '0 2px 40px rgba(0,0,0,0.5)'
              }}>
              
              {slide.headline}
            </h1>

            {/* Body */}
            <p
              className="slide-body text-lg text-[rgba(240,244,255,0.8)] mb-8 max-w-xl leading-relaxed"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 300 }}>
              
              {slide.body}
            </p>

            {/* Buttons */}
            <div className="slide-buttons flex flex-wrap gap-4">
              <button
                onClick={() => handleScroll(slide.cta1.href)}
                className="btn-gradient px-7 py-3.5 rounded-full font-semibold text-sm"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                
                <span>{slide.cta1.label}</span>
              </button>
              <button
                onClick={() => handleScroll(slide.cta2.href)}
                className="btn-outline px-7 py-3.5 rounded-full font-semibold text-sm"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                
                {slide.cta2.label}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Controls */}
      <div className="absolute bottom-[80px] left-0 right-0 z-30">
        <div className="max-w-[1280px] mx-auto px-6 flex items-end justify-between">
          {/* Slide Dots (numbered) */}
          <div className="flex items-center gap-3">
            {slides.map((_, i) =>
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`slider-dot ${i === current ? 'active' : ''}`} />

            )}
            <span className="ml-3 text-xs text-muted font-medium" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {String(current + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
            </span>
          </div>

          {/* Prev / Next */}
          <div className="flex gap-3">
            <button
              onClick={prev}
              aria-label="Previous slide"
              className="w-11 h-11 rounded-full flex items-center justify-center glass transition-all duration-300 hover:border-cyan"
              style={{ border: '1px solid rgba(255,255,255,0.15)' }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(0,194,255,0.5)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'}>
              
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f0f4ff" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              onClick={next}
              aria-label="Next slide"
              className="w-11 h-11 rounded-full flex items-center justify-center glass transition-all duration-300"
              style={{ border: '1px solid rgba(255,255,255,0.15)' }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(0,194,255,0.5)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'}>
              
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f0f4ff" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="absolute bottom-0 left-0 right-0 z-30 stats-bar">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[rgba(255,255,255,0.06)]">
            {stats.map((stat) =>
            <div key={stat.label} className="py-4 px-6 text-center first:pl-0 last:pr-0">
                <div
                className="text-2xl font-extrabold gradient-text"
                style={{ fontFamily: 'Syne, sans-serif' }}>
                
                  {stat.value}
                </div>
                <div
                className="text-xs text-muted mt-0.5 uppercase tracking-[1px]"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700 }}>
                
                  {stat.label}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

}